"use client";

import useSearchRouter from "@/app/hooks/useSearchRouter";
import { ViewMode } from "@/app/hooks/useViewMode";
import ViewToggle from "@/components/view-toggle/viewToggle";
import { options } from "@/configs/constants";
import { FilterType } from "@/types/search";
import { usePathname } from "next/navigation";
import CaretDownIcon from "public/next-public/common/icons/caret-solid-down.svg";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import styles from "./sortByPanel.module.scss";

type SortByPanelProps = {
  pageURI?: string;
  viewModeChangeCallback?: (viewMode: ViewMode) => void;
  toggleFilterPanel?: (filterType: FilterType, event: React.MouseEvent<HTMLButtonElement>) => void;
  viewMode: ViewMode;
};

const SortByPanel = ({ pageURI, viewModeChangeCallback, toggleFilterPanel, viewMode }: SortByPanelProps) => {
  const pathname = usePathname();

  let { sortBy = "net32_products", modifySortBy } = useSearchRouter();

  if (sortBy == "" && pageURI && pageURI.includes("new-arrivals")) {
    sortBy = "net32_new_arrivals"; // Default to net32_new_arrivals if pageURI is  new-arrivals
  }

  const [localSortBy, setLocalSortBy] = useState<string>(sortBy);

  useEffect(() => {
    setLocalSortBy(sortBy);
  }, [sortBy]);

  const selectedOption = options.find((opt) => opt.value === localSortBy) ?? options[0];
  const selectedValue = selectedOption?.value ?? "";
  const selectedLabel = selectedOption?.label ?? "";

  const toggleViewMode = (event: React.MouseEvent<HTMLButtonElement>) => {
    const actionValue = (event.currentTarget as HTMLElement).dataset.view;
    if (actionValue === "grid" || actionValue === "list") {
      // Call callback if provided
      if (viewModeChangeCallback) {
        viewModeChangeCallback(actionValue);
      }
    }
  };

  const onChangeCallback = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const newValue = event.target.value;
      setLocalSortBy(newValue);
      modifySortBy(newValue); // Update URL
    },
    [modifySortBy],
  );

  return (
    <div className={styles.sortByPanel}>
      <div className="d-flex justify-content-between align-items-center">
        <div id="sortByPanel" className="d-flex align-items-center gap-2" aria-describedby="search-sort-with-popover">
          <span className={`d-none d-md-block ${styles.sortByLabel}`}>Sort By:</span>
          <div className={styles.selectWrapper} data-value={selectedLabel}>
            <select className={styles.sortBySelect} name="sortBy" value={selectedValue} onChange={onChangeCallback}>
              {options.map((opt) => (
                <option key={opt.value} value={opt.value} className={styles.sortByOption}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className={styles.sortByCaret} aria-hidden="true">
              <CaretDownIcon width={12} height={12} />
            </span>
          </div>
        </div>
        <div className="d-none align-items-center d-lg-flex gap-3">
          <ViewToggle viewMode={viewMode} toggleViewMode={toggleViewMode} />
        </div>
      </div>
      {toggleFilterPanel && (
        <div className={`d-lg-none ${styles["filtersMenu"]}`}>
          <button
            className={`${styles.mobileFilterBtn} ${styles.mainFilter}`}
            onClick={(evt) => toggleFilterPanel("default", evt)}
          >
            Filter
            <CaretDownIcon width={12} height={12} aria-hidden />
          </button>
          <button
            className={`${styles.mobileFilterBtn} ${styles.secondaryFilters}`}
            onClick={(evt) => toggleFilterPanel("brand", evt)}
          >
            Brand
            <CaretDownIcon width={12} height={12} aria-hidden />
          </button>
          <button
            className={`${styles.mobileFilterBtn} ${styles.secondaryFilters} ${styles.hideBelowSm}`}
            onClick={(evt) => toggleFilterPanel("manufacturer", evt)}
          >
            Manufacturer
            <CaretDownIcon width={12} height={12} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
};

export default SortByPanel;
